<template>
    <v-form ref="form" v-model="formIsValid">
        <v-container>
            <v-row class="contentRow">
                <v-col cols="12">
					<v-alert type="error">The use of this page requires an official Orixe API Key.</v-alert>
                    <h1>Add Data Unit</h1>
                </v-col>
                <v-col cols="3">
                    <v-subheader>Data Unit Type</v-subheader>
                </v-col>
                <v-col cols="9">
                    <v-select
                        outlined
                        :rules="typeRules"
                        id="typeSelect"
                        v-model="type"
                        :items="types"
                        label="Select type..."
                    />
                    <label
                        v-if="type !== 'custom'"
                        class="font-italic warning--text"
                    >Make sure you follow the requirements when using another type than 'custom'!</label>
                </v-col>
                <v-col cols="3">
                    <v-subheader>Data</v-subheader>
                </v-col>
                <v-col cols="9">
                    <v-textarea
                        outlined
                        label="Input data here..."
                        class="border"
                        id="dataInput"
                        v-model="data"
                        :hint="(isDataJSON() || this.type == 'custom') ? '' : 'For other types than custom, data has to be in JSON'"
                        :rules="dataRules"
                    ></v-textarea>
                </v-col>
                <v-col cols="3">
                    <v-subheader>API Key</v-subheader>
                </v-col>
                <v-col cols="9">
                    <v-text-field
                        label="API Key..."
                        outlined
                        id="apiKeyInput"
                        v-model="apikey"
                        :rules="apikeyRules"
                    />
                </v-col>
            </v-row>
            <v-row class="contentRow">
                <v-col>
                    <h2 class="mb-3">Summary</h2>
                    <v-card class="mx-auto" outlined>
                        <v-list-item>
                            <v-list-item-content>
                                <v-list-item-subtitle>Type {{type ? "" : "(empty)"}}</v-list-item-subtitle>
                                <v-list-item-title>{{type}}</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <v-list-item-subtitle>Data {{data ? "" : "(empty)"}}</v-list-item-subtitle>
                                <v-list-item-title>{{data}}</v-list-item-title>
                                <span
                                    v-if="isDataJSON() && data"
                                    class="success--text"
                                >Data is a valid JSON Object!</span>
                                <span
                                    v-if="!(isDataJSON() && data)"
                                    class="warning--text"
                                >Data is not a valid JSON Object!</span>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <v-list-item-subtitle>API Key {{apikey ? "" : "(empty)"}}</v-list-item-subtitle>
                                <v-list-item-title>{{apikey}}</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-card>
                    <h3 class="mt-3">This is what your request will look like:</h3>
                    <pre>
            <code class="d-block">
              Headers:
              X-API-KEY={{apikey}}

              Request body:
              {
                "type": "{{type}}",
                "data": {{(isDataJSON() && data) ? data : `"${data ? data : ''}"`}}
              }
            </code>
          </pre>

                    <v-btn
                        color="success"
                        :disabled="!formIsValid"
                        v-on:click="sendRequest"
                        v-if="!isSending"
                    >
                        <span>Send Request</span>
                    </v-btn>
                    <v-btn color="error" class="ml-3" v-on:click="resetForm" v-if="!isSending">
                        <span>Reset Form</span>
                    </v-btn>
                    <div
                        v-if="!formIsValid"
                        class="mt-2 font-italic secondary--text"
                    >Please correct the input data before sending the request</div>

                    <v-dialog v-model="isSending" hide-overlay persistent width="300">
                        <v-card color="primary" dark>
                            <v-card-text>
                                <p class="pt-3">Please stand by</p>
                                <v-progress-linear indeterminate color="white" class="mb-0 mt-3"></v-progress-linear>
                            </v-card-text>
                        </v-card>
                    </v-dialog>
                    <v-spacer />
                    <v-progress-circular v-if="isSending" indeterminate></v-progress-circular>
                </v-col>
                <results-dialog
                    :result="result"
                    message="We got your data! It's on its way to the blockchain!"
                />
                <error-dialog :error="error" />
            </v-row>
        </v-container>
    </v-form>
</template>

<script>
import API from "../../api/api"

export default {
    name: "AddDataUnit",
    data: () => ({
        test: null,
        type: null,
        data: null,
        apikey: null,
        isSending: false,
        result: null,
        formIsValid: true,
        error: null,
        types: [
            { text: "Custom", value: "custom" },
            { text: "Orixe Transaction", value: "tx" },
            { text: "Orixe Audit", value: "audit" },
            { text: "ESG Form", value: "esg" },
            { text: "DUDE", value: "dude" },
            { text: "Authorized Node", value: "authorized" }
        ],
        typeRules: [v => !!v || "Type is required"],
        apikeyRules: [v => !!v || "API Key is required"]
    }),
    computed: {
        dataRules: function() {
            return [
                v => !!v || "Data is required",
                () =>
                    this.isDataJSON() ||
                    this.type == "custom" ||
                    "Data must be a valid JSON Object if type is 'custom'!"
            ];
        }
    },
    methods: {
        isJSON(i) {
            try {
                JSON.parse(i);
                return true;
            } catch (e) {
                return false;
            }
        },
        isDataJSON() {
            return this.isJSON(this.data);
        },
        getSubmissionResult(response) {
            const items = response && response.data && Array.isArray(response.data.data)
                ? response.data.data
                : [];
            return items.length > 0 && items[0] && typeof items[0] === "object"
                ? items[0]
                : null;
        },
        resetForm() {
            this.$refs.form.reset();
        },
        sendRequest: function() {
            this.isSending = true;
            this.error = null;
            let postData = {
                data: this.isDataJSON() ? JSON.parse(this.data) : this.data,
                type: this.type
            };
            let headers = {
                "X-API-KEY": this.apikey
            };
            API
                .post("/api/dataunits/", postData, {
                    headers: headers
                })
                .then(response => {
                    this.isSending = false;
                    if (response.data.error.length > 0) {
                        this.error = response.data.error[0].message;
                        return;
                    }
                    const submissionResult = this.getSubmissionResult(response);
                    this.result = response.data;
                    this.resetForm();
                    if (submissionResult && submissionResult.duplicate === true && submissionResult.blockIndex !== null && typeof submissionResult.blockIndex !== "undefined") {
                        const blockIndex = Number(submissionResult.blockIndex);
                        if (Number.isFinite(blockIndex)) {
                            this.$router.push({ path: `/blocks/${blockIndex}` });
                        }
                    }
                })
                .catch(reason => {
                    console.error("[AddDataUnit] Error pushing to blockchain:", reason);
                    // Handle 426 Upgrade Required error specifically
                    if (reason.status === 426 || reason.code === 426 || (reason.response && reason.response.status === 426)) {
                        this.error = "Protocol upgrade error (426): The server requires HTTP protocol. Please check your connection settings and ensure you're not trying to use WebSocket for API requests.";
                    } else if (reason.message) {
                        this.error = reason.message;
                    } else if (reason.response && reason.response.data) {
                        if (reason.response.data.error && Array.isArray(reason.response.data.error) && reason.response.data.error.length > 0) {
                            this.error = reason.response.data.error[0].message || "Failed to push to blockchain";
                        } else {
                            this.error = reason.response.data.message || "Failed to push to blockchain";
                        }
                    } else {
                        this.error = reason.toString() || "Failed to push to blockchain. Please try again.";
                    }
                    this.isSending = false;
                });
        },
        setSendingFalse: function() {
            this.isSending = false;
        }
    }
};
</script>
